import { useCallback, useEffect, useRef, useState } from "react";
import type { RandomParams, Video } from "@/types/video";
import { fetchRandom } from "@/services/api";

// Maintains currentVideo + nextVideo (preload) per spec #34.

const RECENT_LIMIT = 30;

interface UseRandomVideoResult {
  current: Video | null;
  next: Video | null;
  previous: Video | null;
  loading: boolean;
  error: string | null;
  channelSwitching: boolean;
  changeChannel: (blockedTags?: string[]) => Promise<void>;
  goPrevious: () => void;
  refreshParams: (params: RandomParams) => void;
}

export function useRandomVideo(initialParams: RandomParams): UseRandomVideoResult {
  const [params, setParams] = useState<RandomParams>(initialParams);
  const paramsRef = useRef<RandomParams>(initialParams);
  const [current, setCurrent] = useState<Video | null>(null);
  const [next, setNext] = useState<Video | null>(null);
  const [previous, setPrevious] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channelSwitching, setChannelSwitching] = useState(false);

  const recentRef = useRef<string[]>([]);
  const switchTimerRef = useRef<number | null>(null);

  const updateRecent = (bvid: string) => {
    const list = recentRef.current.filter((id) => id !== bvid);
    list.unshift(bvid);
    recentRef.current = list.slice(0, RECENT_LIMIT);
  };

  const fetchNext = useCallback(
    async (excludeList: string[]): Promise<Video | null> => {
      try {
        const video = await fetchRandom(paramsRef.current, excludeList);
        return video;
      } catch (e) {
        setError("SIGNAL_LOST");
        return null;
      }
    },
    []
  );

  // Initial load + preload next
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    (async () => {
      const [first, preload] = await Promise.all([
        fetchNext([]),
        fetchNext([]),
      ]);
      if (!mounted) return;
      if (first) {
        setCurrent(first);
        updateRecent(first.bvid);
      }
      if (preload && preload.bvid !== first?.bvid) {
        setNext(preload);
        updateRecent(preload.bvid);
      } else {
        // rare collision, fetch once more
        const again = await fetchNext(recentRef.current);
        if (again && mounted) {
          setNext(again);
          updateRecent(again.bvid);
        }
      }
      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, [fetchNext]);

  // Preload new next whenever current changes
  useEffect(() => {
    if (!current) return;
    if (next) return;
    let mounted = true;
    (async () => {
      const candidate = await fetchNext(recentRef.current);
      if (!mounted) return;
      if (candidate) {
        setNext(candidate);
        updateRecent(candidate.bvid);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [current, next, fetchNext]);

  const changeChannel = useCallback(async (blockedTags = params.blockedTags ?? []) => {
    setChannelSwitching(true);
    setError(null);

    // Brief switch delay for the CRT effect to be visible
    if (switchTimerRef.current) window.clearTimeout(switchTimerRef.current);
    switchTimerRef.current = window.setTimeout(async () => {
      let promoted = next ?? await fetchNext(recentRef.current);
      if (!promoted) {
        setChannelSwitching(false);
        return;
      }
      for (let attempt = 0; attempt < 3 && promoted.tags.some((tag) => blockedTags.includes(tag)); attempt++) {
        const replacement = await fetchNext(recentRef.current);
        if (!replacement) break;
        promoted = replacement;
      }
      if (promoted.tags.some((tag) => blockedTags.includes(tag))) {
        setChannelSwitching(false);
        return;
      }

      setPrevious(current);
      setCurrent(promoted);
      updateRecent(promoted.bvid);

      // Immediately promote next → current and prefetch a new next
      setNext(null);
      const exclude = recentRef.current;
      const candidate = await fetchNext(exclude);
      if (candidate) {
        setNext(candidate);
        updateRecent(candidate.bvid);
      }
      setChannelSwitching(false);
    }, 280);
  }, [current, next, fetchNext, params.blockedTags]);

  const goPrevious = useCallback(() => {
    if (!previous) return;
    setChannelSwitching(true);
    if (switchTimerRef.current) window.clearTimeout(switchTimerRef.current);
    switchTimerRef.current = window.setTimeout(() => {
      const oldCurrent = current;
      setCurrent(previous);
      setPrevious(oldCurrent);
      // Cycle: old current becomes the next preload target
      if (oldCurrent) setNext(oldCurrent);
      setChannelSwitching(false);
    }, 280);
  }, [current, previous]);

  const refreshParams = useCallback((p: RandomParams) => {
    paramsRef.current = p;
    setParams(p);
    // Keep the current channel playing, but discard a preload made with the
    // previous filters so the next user-requested switch uses the new ones.
    setNext(null);
    recentRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      if (switchTimerRef.current) window.clearTimeout(switchTimerRef.current);
    };
  }, []);

  return {
    current,
    next,
    previous,
    loading,
    error,
    channelSwitching,
    changeChannel,
    goPrevious,
    refreshParams,
  };
}
